import {
  ApiCreateRuleConditionRequestDto,
  ApiRuleConditionResponseDto,
  ApiUpdateRuleConditionRequestDto,
  DeviceType,
  ForecastPeriod,
  RepetitionMode,
  RuleConditionComparisonType,
  RuleConditionType,
  RuleConditionWeather,
  WeekDays
} from '@birco/rainmesh-api';
import {RuleConditionFormModel} from '../models/rule-condition-form-model.interface';
import {FormConditionType} from '../enums/form-condition-type';
import {RuleConditionViewModel} from '../models/rule-condition-view-model.interface';

function mapFormToRuleCondition(formType: FormConditionType): RuleConditionType {
  switch (formType) {
    case FormConditionType.cistern:
    case FormConditionType.soil:
      return RuleConditionType.SENSOR_THRESHOLD
    case FormConditionType.clockTime:
      return RuleConditionType.CLOCK_TIME
    case FormConditionType.weather:
      return RuleConditionType.WEATHER
  }
}

function mapRuleToFormCondition(ruleConditionType: RuleConditionType, hasCisternId: boolean): FormConditionType {
  switch (ruleConditionType) {
    case RuleConditionType.SENSOR_THRESHOLD:
      if (hasCisternId) {
        return FormConditionType.cistern
      } else {
        return FormConditionType.soil
      }
    case RuleConditionType.CLOCK_TIME:
      return FormConditionType.clockTime
    case RuleConditionType.WEATHER:
      return FormConditionType.weather
    default:
      throw new Error('not supported')
  }
}

const toValidFrom = (time: string): string => {
  const [hour, minute] = time.split(':').map(Number);

  const now = new Date();
  const next = new Date(now);

  next.setHours(hour, minute, 0, 0);

  if (next <= now) {
    next.setDate(next.getDate() + 1);
  }

  return next.toISOString();
}

export const RuleConditionConverter = {
  toRequestDtoList: (formDtoList: RuleConditionFormModel[]): ApiCreateRuleConditionRequestDto[] | ApiUpdateRuleConditionRequestDto[] => {
    return formDtoList.map((it) => RuleConditionConverter.toRequestDto(it));
  },

  toViewModelList: (responseDtoList: ApiRuleConditionResponseDto[]): RuleConditionViewModel[] => {
    return responseDtoList.map((it) => RuleConditionConverter.toViewModel(it));
  },

  toViewModel: (apiResponseDto: ApiRuleConditionResponseDto): RuleConditionViewModel => {
    const hasCistern = apiResponseDto.sensorDevice?.type === DeviceType.DISTANCE;
    return {
      id: apiResponseDto.id,
      comparisonType: apiResponseDto.comparisonType ?? undefined,
      forecastPeriod: apiResponseDto.forecastPeriod ?? undefined,
      sensorDeviceId: !hasCistern ? apiResponseDto.sensorDevice?.id : undefined,
      sensorDeviceName: !hasCistern ? apiResponseDto.sensorDevice?.name : undefined,
      threshold: apiResponseDto.threshold ?? undefined,
      weather: apiResponseDto.weather ?? undefined,
      time: !!apiResponseDto.validFrom
        ? (() => {
          return new Date(apiResponseDto.validFrom)
        })()
        : undefined,
      type: mapRuleToFormCondition(apiResponseDto.type, hasCistern),
      repetitionMode: apiResponseDto.repetitionMode ?? undefined,
      repetitionInterval: apiResponseDto.repetitionInterval ?? undefined,
      repetitionDays: apiResponseDto.repetitionDays ?? undefined,
      cisternSensorDeviceId: hasCistern ? apiResponseDto.sensorDevice?.id : undefined,
      cisternSensorDeviceName: hasCistern ? apiResponseDto.sensorDevice?.name : undefined,
    };
  },

  toRequestDto: (formDto: RuleConditionFormModel): ApiCreateRuleConditionRequestDto | ApiUpdateRuleConditionRequestDto => {
    const dtoBase = {
      type: mapFormToRuleCondition(formDto.type),
      repetitionMode: RepetitionMode[formDto.repetitionMode as keyof typeof RepetitionMode],
      // repetitionInterval: formDto.repetitionInterval,
      repetitionDays: formDto.repetitionDays ? Object.entries(formDto.repetitionDays)
        .filter(([_, value]) => value)
        .map(([key]) => key as WeekDays) : undefined,
      validFrom: !!formDto.time
        ? toValidFrom(formDto.time)
        : undefined,
      sensorDeviceId: formDto.sensorDeviceId || formDto.cisternSensorDeviceId,
      comparisonType: RuleConditionComparisonType[formDto.comparisonType as keyof typeof RuleConditionComparisonType],
      threshold: formDto.threshold === undefined ? undefined : formDto.threshold / 100, // Handle percentages as fractions
      weather: RuleConditionWeather[formDto.weather as keyof typeof RuleConditionWeather],
      forecastPeriod: ForecastPeriod[formDto.forecastPeriod as keyof typeof ForecastPeriod]
    };

    const dto: ApiCreateRuleConditionRequestDto | ApiUpdateRuleConditionRequestDto = formDto.id
      ? {
        ...dtoBase,
        id: formDto.id,
      }
      : {
        ...dtoBase,
        clientStubId: crypto.randomUUID(),
      };

    // Set default values for these fields, since this feature was removed on 2025-10-08
    if (dto.repetitionMode !== RepetitionMode.NEVER) {
      dto.repetitionInterval = 1;
      // if (dto.repetitionMode === RepetitionMode.WEEKLY) {
      //   const currentDay = (new Date()).getDay();
      //   dto.repetitionDays = [Object.values(WeekDays)[currentDay]];
      // }
    }

    return dto;
  },

  toFormModelList: (responseDtoList: ApiRuleConditionResponseDto[]): RuleConditionFormModel[] => {
    return responseDtoList.map((it) => RuleConditionConverter.toFormModel(it));
  },

  toFormModel: (responseDto: ApiRuleConditionResponseDto): RuleConditionFormModel => {
    const hasCistern = responseDto.sensorDevice?.type === DeviceType.DISTANCE;

    const repetitionDays = Object.fromEntries(
      (Object.values(WeekDays) as WeekDays[]).map((day) => [
        day,
        responseDto?.repetitionDays?.includes(day) ?? false
      ])
    ) as Record<WeekDays, boolean>;

    return {
      id: responseDto.id,
      comparisonType: responseDto.comparisonType ?? undefined,
      forecastPeriod: responseDto.forecastPeriod ?? undefined,
      sensorDeviceId: !hasCistern ? responseDto.sensorDevice?.id : undefined,
      sensorDeviceName: !hasCistern ? responseDto.sensorDevice?.name : undefined,
      threshold: responseDto.threshold === undefined || responseDto.threshold === null ? undefined : responseDto.threshold * 100,
      weather: responseDto.weather ?? undefined,
      time: !!responseDto.validFrom
        ? (() => {
          const asDate = new Date(responseDto.validFrom);
          const hours = String(asDate.getHours()).padStart(2, '0');
          const minutes = String(asDate.getMinutes()).padStart(2, '0');
          return `${hours}:${minutes}`;
        })()
        : undefined,
      type: mapRuleToFormCondition(responseDto.type, hasCistern),
      repetitionMode: responseDto.repetitionMode ?? undefined,
      repetitionInterval: responseDto.repetitionInterval ?? undefined,
      repetitionDays: repetitionDays,
      cisternSensorDeviceId: hasCistern ? responseDto.sensorDevice?.id : undefined,
      cisternSensorDeviceName: hasCistern ? responseDto.sensorDevice?.name : undefined,
    }
  }
}
